import React, { useEffect, useRef, useState } from "react";
import api, { formatApiError, mediaUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Camera, Save, Film, Music2, Share2 } from "lucide-react";
import { toast } from "sonner";
import VideoCard from "@/components/VideoCard";
import AudioCard from "@/components/AudioCard";
import ShareDialog from "@/components/ShareDialog";

export default function Profile() {
  const { user, refreshUser } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [bio, setBio] = useState(user?.bio || "");
  const [videos, setVideos] = useState([]);
  const [audios, setAudios] = useState([]);
  const [saving, setSaving] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const fileRef = useRef(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const shareUrl = user?.handle ? `${window.location.origin}/u/${user.handle}` : "";
  const ogUrl = user?.handle ? `${BACKEND_URL}/api/og/u/${user.handle}` : "";

  useEffect(() => {
    if (!user) return;
    setName(user.name || "");
    setBio(user.bio || "");
    (async () => {
      const [v, a] = await Promise.all([
        api.get(`/videos?owner_id=${user.id}`),
        api.get(`/audios?owner_id=${user.id}`),
      ]);
      setVideos(v.data); setAudios(a.data);
    })();
  }, [user?.id]);

  const saveProfile = async () => {
    setSaving(true);
    try {
      await api.put("/users/me", { name, bio });
      await refreshUser();
      toast.success("Profile updated");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const onAvatar = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post("/users/me/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refreshUser();
      toast.success("Avatar updated");
    } catch (er) { toast.error(formatApiError(er)); }
  };

  return (
    <div className="p-6 sm:p-10 max-w-6xl mx-auto">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.35em] text-[#ff4500] font-mono">── Profile</div>
          <h1 className="font-display text-4xl sm:text-5xl mt-3 tracking-tighter">Your stage</h1>
        </div>
        {user?.handle && (
          <button
            onClick={() => setShareOpen(true)}
            data-testid="promote-share-btn"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#ff4500] hover:bg-[#ff5722] text-white text-xs uppercase tracking-[0.2em]"
          >
            <Share2 className="w-4 h-4" /> Promote · share my page
          </button>
        )}
      </div>

      {user?.handle && (
        <div className="mt-6 bg-[#141414] border border-[#2a2a2a] p-4 flex items-center justify-between flex-wrap gap-3" data-testid="promote-banner">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#a3a3a3]">Your public link</div>
            <div className="font-mono text-sm truncate text-[#f5f5f5]">{shareUrl}</div>
          </div>
          <button onClick={() => setShareOpen(true)} className="px-4 py-2 text-xs uppercase tracking-[0.2em] border border-[#2a2a2a] hover:border-[#ff4500]" data-testid="promote-open-share">
            Get share kit
          </button>
        </div>
      )}

      <div className="mt-10 grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-4">
          <div className="bg-[#141414] border border-[#2a2a2a] p-6">
            <div className="relative w-32 h-32 mx-auto bg-[#1a1a1a] border border-[#2a2a2a] overflow-hidden">
              {user?.avatar_url ? (
                <img src={mediaUrl(user.avatar_url)} alt="" className="w-full h-full object-cover" data-testid="profile-avatar-img" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <span className="font-display text-4xl text-[#ff4500]">{user?.name?.[0]?.toUpperCase() || "U"}</span>
                </div>
              )}
              <button onClick={() => fileRef.current?.click()} data-testid="upload-avatar-btn"
                className="absolute bottom-2 right-2 w-9 h-9 bg-[#ff4500] hover:bg-[#ff5722] flex items-center justify-center">
                <Camera className="w-4 h-4 text-white" />
              </button>
              <input ref={fileRef} type="file" accept="image/*" hidden onChange={onAvatar} data-testid="avatar-file-input" />
            </div>

            <div className="mt-6 space-y-3">
              <div>
                <label className="text-[10px] uppercase tracking-[0.25em] text-[#a3a3a3]">Name</label>
                <input value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-name-input"
                  className="mt-2 w-full bg-[#0a0a0a] border border-[#2a2a2a] focus:border-[#ff4500] outline-none px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.25em] text-[#a3a3a3]">Bio</label>
                <textarea value={bio} onChange={(e) => setBio(e.target.value)} rows={3} data-testid="profile-bio-input"
                  className="mt-2 w-full bg-[#0a0a0a] border border-[#2a2a2a] focus:border-[#ff4500] outline-none px-3 py-2 text-sm resize-none" />
              </div>
              <button onClick={saveProfile} disabled={saving} data-testid="save-profile-btn"
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#ff4500] hover:bg-[#ff5722] disabled:opacity-50 text-white text-sm">
                <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save profile"}
              </button>
            </div>

            <div className="mt-6 pt-6 border-t border-[#2a2a2a] text-xs text-[#a3a3a3] space-y-1 font-mono uppercase tracking-[0.15em]">
              <div>Email · {user?.email}</div>
              <div>Joined · {user?.created_at ? new Date(user.created_at).toLocaleDateString() : "—"}</div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-8 space-y-10">
          <section>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-2xl tracking-tight flex items-center gap-2"><Film className="w-5 h-5 text-[#ff4500]" /> Your videos</h2>
              <span className="text-xs font-mono uppercase tracking-[0.2em] text-[#a3a3a3]">{videos.length} total</span>
            </div>
            {videos.length ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {videos.map((v) => <VideoCard key={v.id} video={v} />)}
              </div>
            ) : <Empty text="No videos uploaded yet." />}
          </section>

          <section>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-2xl tracking-tight flex items-center gap-2"><Music2 className="w-5 h-5 text-[#ff4500]" /> Your tracks</h2>
              <span className="text-xs font-mono uppercase tracking-[0.2em] text-[#a3a3a3]">{audios.length} total</span>
            </div>
            {audios.length ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {audios.map((a) => <AudioCard key={a.id} audio={a} queue={audios} />)}
              </div>
            ) : <Empty text="No tracks uploaded yet." />}
          </section>
        </div>
      </div>

      {shareOpen && user?.handle && (
        <ShareDialog
          title={`${user.name} on Rasin · Haiti's Stage`}
          text={user.bio || `Check out ${user.name} on Rasin 🇭🇹`}
          shareUrl={shareUrl}
          ogUrl={ogUrl}
          onClose={() => setShareOpen(false)}
        />
      )}
    </div>
  );
}

function Empty({ text }) {
  return <div className="border border-dashed border-[#2a2a2a] p-10 text-center text-sm text-[#a3a3a3]">{text}</div>;
}
